import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filters, DataTable, Page, Card, Link, Badge, Tooltip, Button } from '@shopify/polaris';
import { useUrl } from '../../shared/util/hanldesave';
import { formatToCurrency, getTableFooter, truncate, getModalButton, fetchTableData, getRequest, getUserOrg, filtersToApiUrl } from '../../shared/components/functions.jsx';
import { applyFilters, addFilters, handleFilterChange, removeFilter, handleFiltersClearAll, getUserOptions } from '../../shared/components/listFilter.jsx';
import ExcelExporterModal from '../../shared/components/modals/excelExporterModal.jsx';
import { emailSentBadge } from '../../shared/components/badges/badges.jsx';
import QuickViewModal from '../../shared/components/modals/quickViewModal.jsx';
import { useDebounce } from '../../shared/components/inputs/useDebounce.js';
import { useAuthStore, useLookupStore } from '../../context/useStore.js';
import SharePointPdfPreviewButton from '../../shared/components/SharePointPdfPreviewButton.jsx';

// linked_invoices comes from the API as a JSON string (FOR JSON PATH) or NULL when no incoming invoice is linked
function parseLinkedInvoices(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

function derivePurchaseStatusId(item) {
  if (item.PurchaseStatusID) return Number(item.PurchaseStatusID);
  if (item.Geannuleerd) return 5;
  if (item.Ontvangen) return 4;
  if (item.Verstuurd) return 2;
  return 1;
}

function InlineDateCell({ value, emptyLabel = 'Set', disabled = false, saving = false, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    if (!editing) setDraft(value || '');
  }, [value, editing]);

  const commit = async () => {
    setEditing(false);
    const nextValue = draft || null;
    const previousValue = value || null;
    if (nextValue === previousValue) return;
    await onSave(nextValue);
  };

  const cancel = () => {
    setDraft(value || '');
    setEditing(false);
  };

  if (editing) {
    return (
      <input
        autoFocus
        type="date"
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void commit();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        style={{
          width: '132px',
          height: '28px',
          fontSize: '12px',
          lineHeight: '16px',
          padding: '2px 6px',
          border: '1px solid #c9cccf',
          borderRadius: '6px',
          boxSizing: 'border-box'
        }}
      />
    );
  }

  if (disabled) return value || '';

  return (
    <button
      type="button"
      disabled={saving}
      onClick={() => setEditing(true)}
      style={{
        border: 'none',
        background: 'transparent',
        padding: 0,
        margin: 0,
        cursor: saving ? 'default' : 'pointer',
        color: value ? '#202223' : '#6d7175',
        font: 'inherit',
        whiteSpace: 'nowrap'
      }}
      title={value ? 'Edit date' : 'Set date'}
    >
      {saving ? 'Saving...' : value || emptyLabel}
    </button>
  );
}

function getPurchaseStatusTone(statusId) {
  if (statusId === 5) return 'critical';
  if (statusId === 4) return 'success';
  if (statusId === 3) return 'attention';
  if (statusId === 2) return 'info';
  return undefined;
}

const OPEN_PURCHASE_STATUS_IDS = ['1', '2', '3']; // Draft, Sent, Partially received

function getDaysUntil(dateString) {
  if (!dateString) return null;
  const target = new Date(`${dateString}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target - today) / (24 * 60 * 60 * 1000));
}

function cddCountdownBadge(dateString, statusId) {
  if (statusId === 4 || statusId === 5) return null; // received or cancelled
  const days = getDaysUntil(dateString);
  if (days === null) return null;

  if (days < 0) {
    return <Badge tone="critical" size="small">{`${Math.abs(days)}d late`}</Badge>;
  }
  if (days === 0) {
    return (
      <Badge tone="warning" size="small">
        today
      </Badge>
    );
  }
  return <Badge tone={days <= 7 ? 'attention' : 'info'} size="small">{`${days}d`}</Badge>;
}

function InlineStatusCell({ value, label, options, saving = false, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');

  useEffect(() => {
    if (!editing) setDraft(value || '');
  }, [value, editing]);

  const cancel = () => {
    setDraft(value || '');
    setEditing(false);
  };

  const commit = async (nextValue) => {
    setEditing(false);
    const parsedValue = nextValue ? Number(nextValue) : null;
    if (parsedValue === value) return;
    await onSave(parsedValue);
  };

  if (editing) {
    return (
      <select
        autoFocus
        value={draft}
        disabled={saving}
        onChange={(e) => {
          const nextValue = e.target.value;
          setDraft(nextValue);
          void commit(nextValue);
        }}
        onBlur={cancel}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
        style={{
          width: '150px',
          height: '28px',
          fontSize: '12px',
          lineHeight: '16px',
          padding: '2px 6px',
          border: '1px solid #c9cccf',
          borderRadius: '6px',
          boxSizing: 'border-box',
          background: '#fff'
        }}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    );
  }

  return (
    <button
      type="button"
      disabled={saving}
      onClick={() => setEditing(true)}
      style={{
        border: 'none',
        background: 'transparent',
        padding: 0,
        margin: 0,
        cursor: saving ? 'default' : 'pointer'
      }}
      title="Edit status"
    >
      <Badge tone={getPurchaseStatusTone(value)}>{saving ? 'Saving...' : label}</Badge>
    </button>
  );
}

function SortableHeading({ label, tooltip, direction, onToggle }) {
  const suffix = direction === 'asc' ? ' ↑' : direction === 'desc' ? ' ↓' : '';

  return (
    <Tooltip content={tooltip}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          border: 'none',
          background: 'transparent',
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          font: 'inherit',
          fontWeight: 700,
          color: '#202223',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
        {suffix}
      </button>
    </Tooltip>
  );
}

export default function PurchasesPage() {
  const url = useUrl();
  const navigate = useNavigate();
  const organisations = useLookupStore((state) => state.organisations);
  const users = useLookupStore((state) => state.users);
  const { user } = useAuthStore();
  const userOrg = getUserOrg(organisations, user.ORGANISATION_NAME);
  const filteredUsers = getUserOptions(users, userOrg);

  const [items, setItems] = useState([]);
  const [purchaseStatuses, setPurchaseStatuses] = useState([]);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [savingCells, setSavingCells] = useState({});
  // Polaris <Filters> only shows pills for filters applied through its own popover UI; bump this key
  // to remount it after setting filters programmatically so it re-reads the applied filters.
  const [filtersRemountKey, setFiltersRemountKey] = useState(0);
  const [filters, setFilters] = useState({
    q: { type: 'text', name: 'Q', value: url.get('q') || '' },
    sort_by: { type: 'none', name: 'Sort by', value: url.get('sort_by') || null },
    dateFrom: { type: 'date', name: 'Order date from', value: url.get('dateFrom') || '' },
    dateTo: { type: 'date', name: 'Order date to', value: url.get('dateTo') || '' },
    supplier: { type: 'text', name: 'Supplier', value: url.get('supplier') || '' },
    customer: { type: 'text', name: 'Customer', value: url.get('customer') || '' },
    project: { type: 'text', name: 'Project', value: url.get('project') || '' },
    refCustomer: { type: 'text', name: 'Reference customer', value: url.get('refCustomer') || '' },
    dateCustomerFrom: { type: 'date', name: 'Date customer from', value: url.get('dateCustomerFrom') || '' },
    dateCustomerTo: { type: 'date', name: 'Date customer to', value: url.get('dateCustomerTo') || '' },
    deliveryDateFrom: { type: 'date', name: 'Delivery date from', value: url.get('deliveryDateFrom') || '' },
    deliveryDateTo: { type: 'date', name: 'Delivery date to', value: url.get('deliveryDateTo') || '' },
    confirmedDeliveryDateFrom: { type: 'date', name: 'Confirmed delivery date from', value: url.get('confirmedDeliveryDateFrom') || '' },
    confirmedDeliveryDateTo: { type: 'date', name: 'Confirmed delivery date to', value: url.get('confirmedDeliveryDateTo') || '' },
    status: { type: 'multiple', name: 'Status', value: url.get('status') ? JSON.parse(url.get('status')) : null, choices: [] },
    mail: { type: 'status', name: 'PDF emailed', value: url.get('mail') || 'all' },
    owner: { type: 'multiple', name: 'Owner', value: url.get('owner') ? JSON.parse(url.get('owner')) : null, choices: [] }
  });
  const purchaseStatusChoices = purchaseStatuses.map((status) => ({ label: `${status.Status}`, value: `${status.PurchaseStatusID}` }));
  const purchaseStatusSelectOptions = purchaseStatuses.map((status) => ({ label: `${status.Status}`, value: `${status.PurchaseStatusID}` }));
  const displayFilters = {
    ...filters,
    status: { ...filters.status, choices: purchaseStatusChoices },
    owner: { ...filters.owner, choices: filteredUsers }
  };

  const fetchData = async (abortController) => await fetchTableData(page, '/api/purchases', setItems, filters, abortController, setFilterLoading);
  const debouncedFetchData = useDebounce(fetchData, 300);

  const getSortDirection = (field) => {
    const sortValue = filters.sort_by.value;
    if (sortValue === `ASC(${field})`) return 'asc';
    if (sortValue === `DESC(${field})`) return 'desc';
    return null;
  };

  const toggleSort = (field) => {
    const currentDirection = getSortDirection(field);
    const nextSortValue = currentDirection === 'asc' ? `DESC(${field})` : currentDirection === 'desc' ? null : `ASC(${field})`;
    const nextFilters = {
      ...filters,
      sort_by: { ...filters.sort_by, value: nextSortValue }
    };

    setFilters(nextFilters);
    navigate({ pathname: window.location.pathname, search: `${filtersToApiUrl(nextFilters)}` }, { replace: true });
    setPage(1);
  };

  const updatePurchaseDate = async (purchaseId, field, nextValue) => {
    const savingKey = `${purchaseId}:${field}`;
    setSavingCells((current) => ({ ...current, [savingKey]: true }));

    try {
      const response = await fetch(`/api/purchases/${purchaseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: nextValue })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.msg || 'Failed to update purchase date.');
      }

      setItems((currentItems) => currentItems.map((item) => (item.BestellingID === purchaseId ? { ...item, ...data } : item)));
    } catch (error) {
      alert(error.message || 'Failed to update purchase date.');
    } finally {
      setSavingCells((current) => ({ ...current, [savingKey]: false }));
    }
  };

  const updatePurchaseStatus = async (purchaseId, nextStatusId) => {
    const savingKey = `${purchaseId}:PurchaseStatusID`;
    setSavingCells((current) => ({ ...current, [savingKey]: true }));

    try {
      const response = await fetch(`/api/purchases/${purchaseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ PurchaseStatusID: nextStatusId })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.msg || 'Failed to update purchase status.');
      }

      setItems((currentItems) => currentItems.map((item) => (item.BestellingID === purchaseId ? { ...item, ...data } : item)));
    } catch (error) {
      alert(error.message || 'Failed to update purchase status.');
    } finally {
      setSavingCells((current) => ({ ...current, [savingKey]: false }));
    }
  };

  const applyPresetFilters = (updates) => {
    const nextFilters = { ...filters };
    for (const [key, value] of Object.entries(updates)) {
      nextFilters[key] = { ...nextFilters[key], value };
    }

    setFilters(nextFilters);
    setFiltersRemountKey((key) => key + 1);
    navigate({ pathname: window.location.pathname, search: `${filtersToApiUrl(nextFilters)}` }, { replace: true });
    setPage(1);
  };

  const toLocalDateString = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const applyConfirmedDeliveryTodayFilter = () => {
    const today = toLocalDateString(new Date());
    applyPresetFilters({ confirmedDeliveryDateFrom: today, confirmedDeliveryDateTo: today });
  };

  const applyNotReceivedFilter = () => applyPresetFilters({ status: OPEN_PURCHASE_STATUS_IDS });

  const applyOverdueFilter = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    applyPresetFilters({ status: OPEN_PURCHASE_STATUS_IDS, confirmedDeliveryDateFrom: '', confirmedDeliveryDateTo: toLocalDateString(yesterday) });
  };

  useEffect(() => {
    const fetchPurchaseStatuses = async () => {
      const statuses = await getRequest('/api/purchases/statuses');
      if (Array.isArray(statuses)) setPurchaseStatuses(statuses);
    };

    fetchPurchaseStatuses();
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const fetchDataWithAbort = async () => debouncedFetchData(abortController);
    fetchDataWithAbort();
    return () => abortController.abort(); // Cleanup, abort controller when page/filters change
  }, [page, filters]);

  const rows = Array.isArray(items)
    ? items.map((item) => {
        const customerRefDate = item.RefKlantDatum ? String(item.RefKlantDatum).substring(0, 10) : '';
        const deliveryDate = item.DeliveryDate ? String(item.DeliveryDate).substring(0, 10) : '';
        const confirmedDeliveryDate = item.SupplierConfirmedDeliveryDate ? String(item.SupplierConfirmedDeliveryDate).substring(0, 10) : '';
        const statusId = derivePurchaseStatusId(item);
        const statusLabel = purchaseStatuses.find((status) => Number(status.PurchaseStatusID) === statusId)?.Status || item.purchase_status_name || 'Draft';
        const projectDescription = truncate(item.project_description, 10, false);
        const linkedInvoices = parseLinkedInvoices(item.linked_invoices);
        const projectLabel =
          item.project_number && projectDescription
            ? `${item.project_number} | ${projectDescription}`
            : item.project_number || projectDescription || (item.ProjectID ? `Project ${item.ProjectID}` : '');
        const row = [
          <Link removeUnderline url={'/purchases/' + item.BestellingID}>
            {item.Bestellingnummer}
          </Link>,
          getModalButton(item.BestellingID, item.Bestellingnummer, setModalOpen, setSelectedItem),
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
            <SharePointPdfPreviewButton
              companyNumber={item.Klantnummer}
              documentTypeId={13}
              itemNumber={item.Bestellingnummer}
              systemDocType="order"
              languageId={item.TaalID}
              buttonLabel="PDF"
              modalTitle={`Purchase PDF ${item.Bestellingnummer}`}
            />
            <SharePointPdfPreviewButton
              companyNumber={item.Klantnummer}
              documentTypeId={13}
              itemNumber={item.Bestellingnummer}
              fileName={item.SupplierConfirmationFilename}
              buttonLabel="C-PDF"
              tooltipContent="Supplier confirmation PDF"
              modalTitle={`Confirmation PDF ${item.Bestellingnummer}`}
            />
          </div>,
          String(item.Datum).substring(0, 10),
          <Link removeUnderline url={'/companies/' + item.LeverancierID}>
            {truncate(item.supplier_name, 30, false)}
          </Link>,
          <Link removeUnderline url={'/companies/' + item.KlantID}>
            {truncate(item.customer_name, 30, false)}
          </Link>,
          item.ProjectID ? (
            <Link removeUnderline url={'/projects/' + item.ProjectID}>
              {projectLabel}
            </Link>
          ) : (
            ''
          ),
          item.RefKlant,
          <InlineDateCell
            value={customerRefDate}
            emptyLabel="Set"
            saving={Boolean(savingCells[`${item.BestellingID}:RefKlantDatum`])}
            onSave={(nextValue) => updatePurchaseDate(item.BestellingID, 'RefKlantDatum', nextValue)}
          />,
          <InlineDateCell
            value={deliveryDate}
            emptyLabel="Set"
            saving={Boolean(savingCells[`${item.BestellingID}:DeliveryDate`])}
            onSave={(nextValue) => updatePurchaseDate(item.BestellingID, 'DeliveryDate', nextValue)}
          />,
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
            <InlineDateCell
              value={confirmedDeliveryDate}
              emptyLabel="Set"
              saving={Boolean(savingCells[`${item.BestellingID}:SupplierConfirmedDeliveryDate`])}
              onSave={(nextValue) => updatePurchaseDate(item.BestellingID, 'SupplierConfirmedDeliveryDate', nextValue)}
            />
            {cddCountdownBadge(confirmedDeliveryDate, statusId)}
          </div>,
          formatToCurrency(item.total_value),
          item.Afkorting ? <Badge>{item.Afkorting}</Badge> : '',
          <InlineStatusCell
            value={statusId}
            label={statusLabel}
            options={purchaseStatusSelectOptions}
            saving={Boolean(savingCells[`${item.BestellingID}:PurchaseStatusID`])}
            onSave={(nextValue) => updatePurchaseStatus(item.BestellingID, nextValue)}
          />,
          emailSentBadge(item.Verstuurd),
          linkedInvoices.length ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}>
              {linkedInvoices.map((invoice) => (
                <Tooltip key={invoice.id} content={[invoice.reference, invoice.date, formatToCurrency(invoice.amount || 0)].filter(Boolean).join(' · ')}>
                  <Link removeUnderline url={'/invoices_in/' + invoice.id}>
                    {invoice.number || `#${invoice.id}`}
                  </Link>
                </Tooltip>
              ))}
            </div>
          ) : (
            ''
          )
        ];
        // Strike through cancelled orders, like the sales overview does
        if (statusId === 5) return row.map((cell) => <div style={{ textDecoration: 'line-through' }}>{cell}</div>);
        return row;
      })
    : [];

  const totals = items.length > 0 ? ['', '', '', '', '', '', '', '', '', '', '', formatToCurrency(items.reduce((p, c) => p + c.total_value, 0)), '', '', '', ''] : null;

  return (
    <Page title="Purchase orders" fullWidth>
      <QuickViewModal modalOpen={modalOpen} setModalOpen={setModalOpen} id={selectedItem.id} type="default" source="purchases" title={`Purchase #${selectedItem.number}`} />
      <Card>
        <Filters
          key={filtersRemountKey}
          queryValue={filters.q.value}
          filters={addFilters(displayFilters, setFilters, navigate, setPage)}
          appliedFilters={applyFilters(displayFilters, setFilters, navigate, setPage)}
          loading={filterLoading}
          onQueryChange={(e) => handleFilterChange(e, displayFilters, setFilters, 'q', navigate, setPage)}
          onQueryClear={() => removeFilter('q', displayFilters, setFilters, navigate, setPage)}
          autoComplete="off"
          queryPlaceholder="Filter purchases on number, supplier, customer, project, reference, customer date, delivery date and confirmed delivery date"
          onClearAll={() => handleFiltersClearAll(displayFilters, setFilters, navigate, setPage)}
        >
          <Button size="slim" onClick={applyNotReceivedFilter}>
            Not received
          </Button>
          <Button size="slim" onClick={applyOverdueFilter}>
            Overdue
          </Button>
          <Button size="slim" onClick={applyConfirmedDeliveryTodayFilter}>
            CDD today
          </Button>
          <ExcelExporterModal filters={filters} setFilters={setFilters} page={page} setPage={setPage} />
        </Filters>
        <DataTable
          stickyHeader
          increasedTableDensity
          verticalAlign="middle"
          columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'numeric', 'text', 'text', 'text', 'text']}
          totals={totals}
          showTotalsInFooter
          headings={[
            <b>Number</b>,
            <b>View</b>,
            <b>PDF</b>,
            <SortableHeading label="Date" tooltip="Order date" direction={getSortDirection('Bestelling.Datum')} onToggle={() => toggleSort('Bestelling.Datum')} />,
            <b>Supplier</b>,
            <b>Customer</b>,
            <b>Project</b>,
            <b>Reference customer</b>,
            <SortableHeading label="DC" tooltip="Date customer" direction={getSortDirection('Bestelling.RefKlantDatum')} onToggle={() => toggleSort('Bestelling.RefKlantDatum')} />,
            <SortableHeading label="DD" tooltip="Delivery Date" direction={getSortDirection('Bestelling.DeliveryDate')} onToggle={() => toggleSort('Bestelling.DeliveryDate')} />,
            <SortableHeading
              label="CDD"
              tooltip="Confirmed delivery date"
              direction={getSortDirection('Bestelling.SupplierConfirmedDeliveryDate')}
              onToggle={() => toggleSort('Bestelling.SupplierConfirmedDeliveryDate')}
            />,
            <b>Price</b>,
            <b>Owner</b>,
            <b>Status</b>,
            <b>PDF emailed</b>,
            <b>Invoice in</b>
          ]}
          rows={rows}
          footerContent={getTableFooter(rows, page, setPage, 'purchases')}
        />
      </Card>
    </Page>
  );
}
